import { useId } from 'react';
import Select, {
  components as reactSelectComponents,
  type ClassNamesConfig,
  type GroupBase,
  type InputProps,
  type Props as SelectProps,
  type StylesConfig,
} from 'react-select';
import { joinClasses, mergeClassNames } from 'utils/componentStyles/style.utils';
import { baseClassNames } from './config/baseSelect.config';

/**
 * Renders react-select's text input with an extra accessible description merged in.
 *
 * react-select builds the combobox's `aria-describedby` itself (from a fixed list of aria props,
 * pointing at its own placeholder or live region) and drops any `aria-describedby` handed to
 * `Select`, so overriding `Input` is the only seam where a caller's id can be *added* instead of
 * replacing react-select's own. The id travels in `selectProps` so this stays a module-level
 * component: one rebuilt per render is a new component type each time, which remounts the input
 * and loses focus mid-typing.
 *
 * @param props react-select Input props; `selectProps.describedById` carries the extra id.
 * @returns The select's text input.
 */
const DescribedSelectInput = <Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
  props: InputProps<Option, IsMulti, Group>,
): JSX.Element => {
  const { describedById } = props.selectProps as typeof props.selectProps & {
    describedById?: string;
  };
  const describedBy = [props['aria-describedby'], describedById].filter(Boolean).join(' ');

  return <reactSelectComponents.Input {...props} aria-describedby={describedBy || undefined} />;
};

type BaseSelectProps<Option, IsMulti extends boolean = false> = {
  wrapperClassName?: string;
  inputId?: string;
  classNames?: ClassNamesConfig<Option, IsMulti, GroupBase<Option>>;
  styles?: StylesConfig<Option, IsMulti, GroupBase<Option>>;
  label?: string;
  isMulti?: IsMulti;
  isSearchable?: boolean;
  isClearable?: boolean;
  closeMenuOnSelect?: boolean;
  maxMenuHeight?: number;
  useBaseClassNames?: boolean;
  /**
   * Render the menu in a portal on `document.body` (the default).
   *
   * react-select draws its menu inline, so inside a modal, a card or any
   * `overflow: hidden` container it gets clipped and looks like the control does
   * nothing. Pass false only when the select lives in its own stacking context
   * and the inline menu is known to fit.
   */
  portalMenu?: boolean;
  /**
   * Id of an element that further describes the control, appended to the combobox's
   * `aria-describedby`.
   *
   * react-select hard-sets that attribute and ignores an `aria-describedby` prop, so the id can
   * only be merged in by overriding its `Input` component — see `DescribedSelectInput`. It only
   * reaches the DOM on a searchable select: react-select swaps `Input` for an internal dummy
   * input when `isSearchable` is false.
   */
  describedById?: string;
  selectProps?: SelectProps<Option, IsMulti, GroupBase<Option>>;
} & {};

/**
 * Renders a styled react-select control with an optional accessible label.
 *
 * @param props Select wrapper and react-select configuration.
 * @returns The styled select control.
 */
const BaseSelect = <Option, IsMulti extends boolean = false>({
  wrapperClassName = '',
  inputId,
  classNames,
  styles,
  label,
  isMulti,
  isSearchable,
  isClearable,
  closeMenuOnSelect,
  maxMenuHeight = 160,
  useBaseClassNames = true,
  portalMenu = true,
  describedById,
  selectProps,
}: BaseSelectProps<Option, IsMulti>): JSX.Element => {
  const generatedId = useId();
  const resolvedInputId = inputId || generatedId;
  const resolvedClassNames = useBaseClassNames
    ? mergeClassNames(baseClassNames, classNames)
    : classNames;

  // A portalled menu leaves the modal's stacking context, so it needs to sit on the
  // popover layer to paint above it. See Docs/Layering.md.
  const menuPortalTarget = portalMenu && typeof document !== 'undefined' ? document.body : null;
  const resolvedStyles: StylesConfig<Option, IsMulti, GroupBase<Option>> = menuPortalTarget
    ? {
        ...styles,
        menuPortal: (base, state) => ({
          ...base,
          ...(styles?.menuPortal?.(base, state) ?? {}),
          zIndex: 'var(--z-popover)' as unknown as number,
        }),
      }
    : (styles ?? {});

  // The description is merged onto the combobox by an Input override, and the id rides along on
  // the select so that override can read it back out of `selectProps`. A consumer's own
  // `components` bag keeps every entry it set; only `Input` is taken over, and only when there is
  // a description to merge.
  const describedByProps = describedById
    ? ({
        describedById,
        components: { ...selectProps?.components, Input: DescribedSelectInput },
      } as Partial<SelectProps<Option, IsMulti, GroupBase<Option>>>)
    : undefined;

  return (
    <div className={joinClasses('d-flex flex-column gap-1', wrapperClassName)}>
      {label && (
        <label className="fw-medium" htmlFor={resolvedInputId}>
          {label}
        </label>
      )}
      <Select<Option, IsMulti, GroupBase<Option>>
        inputId={resolvedInputId}
        unstyled
        classNames={resolvedClassNames}
        styles={resolvedStyles}
        menuPortalTarget={menuPortalTarget}
        isMulti={isMulti}
        isSearchable={isSearchable}
        isClearable={isClearable}
        closeMenuOnSelect={closeMenuOnSelect ?? !isMulti}
        maxMenuHeight={maxMenuHeight}
        {...selectProps}
        {...describedByProps}
      />
    </div>
  );
};

export default BaseSelect;
